import React, { useCallback, useMemo, useRef, useState } from 'react'

import { DraftingApplyEditRequest } from '@harvey-frontend/openapi/models/DraftingApplyEditRequest'
import { PdfKitSource } from '@harvey-frontend/openapi/models/PdfKitSource'
import { cn } from '@harvey-frontend/utils/utils'
import * as Sentry from '@sentry/react'
import { useShallow } from 'zustand/react/shallow'

import { DraftingEditType } from 'openapi/models/DraftingEditType'
import { UploadedFile } from 'openapi/models/UploadedFile'

import { displayErrorMessage } from 'utils/toast'

import {
  Card,
  CardHeader,
  CardContent,
} from '@harvey-frontend/components/ui/card'
import { ScrollArea } from '@harvey-frontend/components/ui/scroll-area'
import { useAnalytics } from 'components/common/analytics/analytics-context'

import { transformSelection } from '../../../../services/document/edit-utilities'
import { fetchDraftingApply } from '../../../../services/endpoints'
import {
  computeReferenceKey,
  ReferenceWithFile,
} from '../../../file-previewer/utils/helpers'
import { useAccessTokenContext } from '../../providers/AccessTokenProvider'
import { useDialogContext } from '../../providers/DialogProvider'
import useAppStore from '../../stores/store'
import { getUploadedFiles } from '../../utils/file-utils'
import { getTextAroundCursor } from '../../utils/formatting-utils'
import { getSelection } from '../../utils/office'
import { openFilePreviewer } from '../../utils/office'
import { OrderedCitations } from './OrderedCitations'
import { Suggestion } from './Suggestion'

// Stable mapping from all suggestion references to a unique integer.
const generateReferenceIndices = (references: PdfKitSource[]) => {
  return references.reduce<Record<string, number>>((acc, reference) => {
    const citationKey = computeReferenceKey(reference)
    if (!(citationKey in acc)) {
      acc[citationKey] = Object.keys(acc).length + 1
    }
    return acc
  }, {})
}

const Suggestions = () => {
  const {
    suggestions,
    reviewSuggestions,
    files,
    setIsApplyingSuggestion,
    setHighlightedText,
    setQueryError,
    addAcceptedSuggestion,
    queryId,
    editSummary,
  } = useAppStore(
    useShallow((s) => ({
      suggestions: s.suggestions,
      reviewSuggestions: s.reviewSuggestions,
      files: s.files,
      setIsApplyingSuggestion: s.setIsApplyingSuggestion,
      setHighlightedText: s.setHighlightedText,
      setQueryError: s.setQueryError,
      addAcceptedSuggestion: s.addAcceptedSuggestion,
      clearAcceptedSuggestions: s.clearAcceptedSuggestions,
      queryId: s.queryId,
      editSummary: s.editSummary,
    }))
  )
  const { dialogRef, closeDialog } = useDialogContext()
  const { getAccessToken } = useAccessTokenContext()
  const textWithReferences = useMemo(() => {
    if (editSummary) {
      return [
        {
          text: editSummary?.heading ?? '',
          references: editSummary?.references ?? [],
        },
      ]
    } else if (suggestions.length > 0) {
      return suggestions.map((suggestion) => ({
        text: suggestion.suggestion,
        references: suggestion.references ?? [],
      }))
    }
    return []
  }, [editSummary, suggestions])
  const openDialog = useCallback(
    (selectedReference: ReferenceWithFile | null = null) => {
      void openFilePreviewer(
        dialogRef,
        closeDialog,
        getAccessToken,
        textWithReferences,
        getUploadedFiles(files),
        selectedReference
      )
    },
    [dialogRef, closeDialog, getAccessToken, textWithReferences, files]
  )

  const [applyingSuggestionIdx, setApplyingSuggestionIdx] = useState<
    number | null
  >(null)
  const { trackEvent } = useAnalytics()
  const suggestionsDivRef = useRef<HTMLDivElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  const handleApply = async (appliedSuggestionIdx: number) => {
    const suggestionToApply = suggestions[appliedSuggestionIdx]
    if (suggestionToApply.suggestion.length === 0) {
      displayErrorMessage('Suggestion cannot be empty')
      return
    }
    try {
      // First, grab the Word text range this selection tracks.
      // We need to reload the text inside the range in case it changed since
      // originally generated.
      const suggestionHighlight = suggestionToApply.range
      suggestionHighlight.load('text')
      await suggestionHighlight.context.sync()

      // Theoretically shouldn't happen, but safeguard against this object
      // having already been cleaned up.
      if (suggestionHighlight?.isNullObject) {
        console.error('suggestionHighlight is null object')
        return
      }
      setIsApplyingSuggestion(true)
      setApplyingSuggestionIdx(appliedSuggestionIdx)
      setQueryError(null)
      const documentContext = await getTextAroundCursor()
      const request: DraftingApplyEditRequest = {
        id: suggestionToApply.id,
        instructions: suggestionToApply.suggestion,
        textToEdit: suggestionHighlight?.text ?? '',
        documentContext,
        editType: DraftingEditType.DEFAULT,
      }
      const response = await fetchDraftingApply(request)
      const editedText = response.editedText
      await transformSelection(suggestionHighlight, editedText)
      suggestionHighlight?.select()
      await suggestionHighlight?.context.sync()
      await getSelection({
        callback: setHighlightedText,
      })
      addAcceptedSuggestion(appliedSuggestionIdx)
      if (suggestionToApply.dirty) {
        trackEvent('Suggestion Edited', {
          query_id: queryId,
          suggestion_id: appliedSuggestionIdx,
          suggestion_length: suggestionToApply.suggestion.length,
        })
      }
      trackEvent('Suggestion Applied', {
        query_id: queryId,
        suggestion_id: appliedSuggestionIdx,
        suggestion_length: suggestionToApply.suggestion.length,
      })
    } catch (e) {
      console.error(e)
      setQueryError({ reason: 'Error applying suggestion' })
      Sentry.captureException(e)
    } finally {
      setIsApplyingSuggestion(false)
      setApplyingSuggestionIdx(null)
    }
  }

  const referencesToFiles = useMemo(() => {
    return suggestions.reduce<Record<string, UploadedFile>>(
      (acc, suggestion) => {
        if (!suggestion.references) return acc
        suggestion.references.forEach((reference) => {
          const referenceKey = computeReferenceKey(reference)
          const matchingFile = files.find(
            (file) => file.uploadedFile?.id === reference.documentId
          )
          if (!(referenceKey in acc) && matchingFile?.uploadedFile) {
            acc[referenceKey] = matchingFile.uploadedFile
          }
        })
        return acc
      },
      {}
    )
  }, [suggestions, files])

  const referenceIndices = useMemo(() => {
    if (editSummary) {
      return generateReferenceIndices(editSummary?.references ?? [])
    } else if (suggestions.length > 0) {
      return generateReferenceIndices(
        suggestions.flatMap((suggestion) => suggestion.references ?? [])
      )
    }
    return {}
  }, [suggestions, editSummary])

  const appliedEditsSummaryComponent = editSummary ? (
    <Card className="rounded">
      <CardHeader className="p-2 text-sm font-medium">Revisions</CardHeader>
      <CardContent className="p-2 pt-0">
        <div className="mb-1 text-sm">{editSummary?.heading}</div>
        <OrderedCitations
          textWithReferences={{
            text: editSummary.heading,
            references: editSummary.references ?? [],
          }}
          referencesToFiles={referencesToFiles}
          referenceIndices={referenceIndices}
          openDialog={openDialog}
        />
      </CardContent>
    </Card>
  ) : null

  return (
    <div
      className={cn('flex flex-[3] flex-col', {
        'gap-2.5': appliedEditsSummaryComponent,
      })}
    >
      {appliedEditsSummaryComponent}
      <ScrollArea
        maxHeight="max-h-screen"
        ref={scrollAreaRef}
        className={cn({
          'border-0': suggestions.length === 0 || !reviewSuggestions,
          'rounded border': suggestions.length > 0 && reviewSuggestions,
        })}
      >
        {suggestions.length > 0 && reviewSuggestions && (
          <div ref={suggestionsDivRef} className="flex flex-col gap-2 p-2">
            {suggestions.map((suggestion, idx) => (
              <Suggestion
                key={idx}
                idx={idx}
                suggestion={suggestion}
                referenceIndices={referenceIndices}
                referencesToFiles={referencesToFiles}
                isActivelyApplying={applyingSuggestionIdx === idx}
                onAccept={handleApply}
                openDialog={openDialog}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  )
}

export default Suggestions
